import { QueryKey, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { useGetDisplayColumn } from '@/api/display-column'
import {
  DisplayColumnDashboard,
  DisplayColumnField,
  DisplayColumnFields,
  DisplayColumnKey,
  DisplayFieldType,
  PersonDisplay,
} from '@/models/features/display-column.models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'

import {
  DEFAULT_FIELDS_BY_TYPE,
  HIGHLIGHT_DURATION_MS,
  ROLES_ALLOWED_TO_EDIT,
} from './person-column.constants'
import { PersonColumnSettings } from './person-column.types'

export const pickDisplayColumnFields = (fields: DisplayColumnFields): DisplayColumnFields => ({
  apartment: !!fields.apartment,
  avatar: !!fields.avatar,
  buildingNumber: !!fields.buildingNumber,
  email: !!fields.email,
  firstName: true,
  lastName: true,
  phone: !!fields.phone,
  role: !!fields.role,
  street: !!fields.street,
})

// Note: the column's enabled fields as the BE applied them - a field is on when any loaded row's
// `<column>Display` has its key (the BE sends every enabled field, `null` when empty, and leaves
// out the disabled ones). Null when no row has a `<column>Display` (none loaded, or an older BE).
export const getFieldsFromDisplays = (
  displays: (PersonDisplay | null | undefined)[] | undefined,
): DisplayColumnFields | null => {
  const loaded = (displays ?? []).filter(
    (display): display is PersonDisplay => !!display && typeof display === 'object',
  )
  if (!loaded.length) return null
  const has = (field: DisplayColumnField) => loaded.some((display) => field in display)
  return pickDisplayColumnFields({
    apartment: has('apartment'),
    avatar: has('avatar'),
    buildingNumber: has('buildingNumber'),
    email: has('email'),
    firstName: true,
    lastName: true,
    phone: has('phone'),
    role: has('role'),
    street: has('street'),
  })
}

interface UsePersonColumnSettingsParams {
  dashboard: DisplayColumnDashboard
  column: DisplayColumnKey
  fieldType: DisplayFieldType
  // Note: the query key (or its prefix) of the table's rows - set once the page renders from the
  // BE's `<column>Display`, so Save refetches them (see PersonColumnSettings.refreshRows).
  rowsQueryKey?: QueryKey
  // Note: the loaded rows' `<column>Display` objects (`[]` while the rows load). The table takes
  // its fields from them; the saved settings are only fetched when rows came without any. Left
  // out, the column always renders from the saved settings.
  displays?: (PersonDisplay | null | undefined)[]
}

// Note: the "Column data settings" of one person column for the active building. The fields come
// from the rows' `<column>Display` (the BE is the source of truth); the saved settings are only
// fetched for rows without one (an older BE) - the settings modal loads them itself when opened.
// Until either is there the column renders its defaults - the table never waits on them.
export const usePersonColumnSettings = ({
  dashboard,
  column,
  fieldType,
  rowsQueryKey,
  displays,
}: UsePersonColumnSettingsParams): PersonColumnSettings => {
  const queryClient = useQueryClient()
  const activeBuilding = useSelector(selectActiveBuilding)
  const userRole = useSelector(selectUserRole)
  const fieldsFromDisplays = getFieldsFromDisplays(displays)
  // Note: rows loaded without any `<column>Display` (or a page that doesn't pass them) - fall back
  // to the saved settings. No rows yet means nothing to render, so nothing to fetch.
  const needsSavedSettings = !fieldsFromDisplays && (!displays || displays.length > 0)
  const { data } = useGetDisplayColumn(
    { buildingId: activeBuilding?.id, column, dashboard },
    { enabled: needsSavedSettings },
  )
  const [highlighted, setHighlighted] = useState(false)

  useEffect(() => {
    if (!highlighted) return
    const timeout = setTimeout(() => setHighlighted(false), HIGHLIGHT_DURATION_MS)
    return () => clearTimeout(timeout)
  }, [highlighted])

  return {
    canEdit: ROLES_ALLOWED_TO_EDIT.includes(userRole),
    column,
    dashboard,
    fields:
      fieldsFromDisplays ??
      (data ? pickDisplayColumnFields(data) : DEFAULT_FIELDS_BY_TYPE[fieldType]),
    fieldType,
    highlighted,
    onSaved: () => setHighlighted(true),
    refreshRows: rowsQueryKey
      ? () => queryClient.invalidateQueries({ queryKey: rowsQueryKey })
      : undefined,
  }
}
